import {Component, OnDestroy, OnInit} from '@angular/core';
import gql from 'graphql-tag';
import {Apollo} from 'apollo-angular';
import {UntypedFormControl} from '@angular/forms';
import {BehaviorSubject, combineLatest, EMPTY, merge, Observable, of, Subscription} from 'rxjs';
import {catchError, debounceTime, switchMap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {
  GetTemplates,
  GetTemplatesVariables,
  GetTemplates_templates_edges_node,
} from './__generated__/GetTemplates';
import {ImageTypeEnum, RoleType} from 'src/app/shared/graphql/globalTypes';
import {ErrorService} from 'src/app/shared/services/error.service';
import {
  GetTotalTemplateCount,
  GetTotalTemplateCountVariables,
} from './__generated__/GetTotalTemplateCount';
import {TemplateEditMode} from '../../builder/builder-form/builder-form.component';
import {
  labelFrag,
  MultiSelectType,
  tagFrag,
} from '../../../shared/components/multi-select/multi-select.component';
import {
  GetTemplateOverviewQuery,
  GetTemplateOverviewQuery_me,
} from './__generated__/GetTemplateOverviewQuery';
import {MultiSelectComponentLabelFragment} from '../../../shared/components/multi-select/__generated__/MultiSelectComponentLabelFragment';
import {MultiSelectComponentTagFragment} from '../../../shared/components/multi-select/__generated__/MultiSelectComponentTagFragment';

const templateGetQuery = gql`
  query GetTemplates($offset: Int, $first: Int, $filterType: ImageTypeEnum, $search: String, $orderBy: String, $archived: Boolean, $labels: [ID!], $tags: [ID!] ) {
    templates(offset: $offset, first: $first, filterType: $filterType, search: $search, orderBy: [$orderBy], archived: $archived, filterLabels: $labels, filterTags: $tags) {
      totalCount
      edges {
        node {
          id
          created
          thumbnail
          description
          title
          statistics {
            newContentCount
            newViewsCount
          }

          items {
            id
            title
            type
            archived
            builderConfigId

            statistics {
              newContentCount
              newViewsCount
            }
          }
        }
      }
    }
  }
`;

const totalTemplateCountGetQuery = gql`
  query GetTotalTemplateCount($filterType: ImageTypeEnum, $search: String, $archived: Boolean, $labels: [ID!], $tags: [ID!]) {
    templates(filterType: $filterType, search: $search, archived: $archived , filterLabels: $labels, filterTags: $tags) {
      totalCount
    }
  }
`;

const templateOverviewGetQuery = gql`
    query GetTemplateOverviewQuery {
      me {
        id
        role
      }
      labels {
        edges {
          node {
            ...MultiSelectComponentLabelFragment
          }
        }
      }
      tags {
        edges {
          node {
            ...MultiSelectComponentTagFragment
          }
        }
      }
    }
    ${labelFrag}
    ${tagFrag}
`;

@Component({
  selector: 'app-template-overview',
  templateUrl: './template-overview.component.html',
  styleUrls: ['./template-overview.component.scss'],
  standalone: false,
})
export class TemplateOverviewComponent implements OnInit, OnDestroy {
  templates: GetTemplates_templates_edges_node[] = [];
  currentTemplateId = '';
  ImageTypeEnum = ImageTypeEnum;
  selection$ = new BehaviorSubject<ImageTypeEnum | null>(null);
  first = new UntypedFormControl(10);
  offset = new UntypedFormControl(1);
  search = new UntypedFormControl(null);
  labelControl = new UntypedFormControl([]);
  tagControl = new UntypedFormControl([]);
  onlyArchived = new UntypedFormControl(false);
  pageCount = 1;
  pages: number[] = [];
  totalCount: number | null = null;
  loading = true;
  TemplateEditMode = TemplateEditMode;
  MultiSelectType = MultiSelectType;
  RoleType = RoleType;
  currentUser: GetTemplateOverviewQuery_me | null = null;
  labels: MultiSelectComponentLabelFragment[] = [];
  tags: (MultiSelectComponentTagFragment | null)[] = [];
  labelControlOpen = false;
  tagControlOpen = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private apollo: Apollo,
    private errService: ErrorService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const currentPaging$ = merge(
      this.first.valueChanges,
      of(this.first.value),
    ) as Observable<number>;

    const offset$ = merge(this.offset.valueChanges, of(this.offset.value)) as Observable<number>;

    const search$ = merge(this.search.valueChanges, of(this.search.value)).pipe(
      debounceTime(300),
    ) as Observable<string>;

    const onlyArchived$ = merge(
      this.onlyArchived.valueChanges,
      of(this.onlyArchived.value),
    ) as Observable<boolean | null>;

    const filterChange$ = merge(currentPaging$, this.selection$, onlyArchived$);

    const labelChange$ = merge(
      this.labelControl.valueChanges,
      of(this.labelControl.value),
    ) as Observable<MultiSelectComponentLabelFragment[]>;

    const tagChange$ = merge(this.tagControl.valueChanges, of(this.tagControl.value)) as Observable<
      MultiSelectComponentTagFragment[]
    >;

    const content$ = combineLatest([
      currentPaging$,
      this.selection$,
      offset$,
      search$,
      onlyArchived$,
      labelChange$,
      tagChange$,
    ]).pipe(
      debounceTime(50),
      switchMap(([first, filterType, offset, search, archived, labels, tags]) => {
        this.currentTemplateId = '';
        return this.apollo
          .watchQuery<GetTemplates, GetTemplatesVariables>({
            query: templateGetQuery,
            variables: {
              filterType,
              first,
              offset: first * (offset - 1),
              search,
              orderBy: '-created',
              archived,
              labels: labels.map(l => l.id),
              tags: tags.map(t => t.id),
            },
          })
          .valueChanges.pipe(
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    const totalCount$ = combineLatest([
      this.selection$,
      search$,
      onlyArchived$,
      labelChange$,
      tagChange$,
    ]).pipe(
      debounceTime(50),
      switchMap(([filterType, search, archived, labels, tags]) => {
        return this.apollo
          .watchQuery<GetTotalTemplateCount, GetTotalTemplateCountVariables>({
            query: totalTemplateCountGetQuery,
            variables: {
              filterType,
              search,
              archived,
              labels: labels.map(l => l.id),
              tags: tags.map(t => t.id),
            },
          })
          .valueChanges.pipe(
            catchError(error => {
              this.errService.handleDefault(error);
              return EMPTY;
            }),
          );
      }),
    );

    this.subscriptions.push(
      search$.subscribe(() => {
        this.offset.setValue(1);
      }),
      filterChange$.subscribe(() => {
        this.offset.setValue(1);
      }),
      content$.subscribe(output => {
        const templates = output.data?.templates;
        this.templates = (templates?.edges ?? []).map(e => e.node);
        this.loading = false;
        this.pageCount = (templates?.totalCount ?? 0) / this.first.value;
        this.pageCount = Math.ceil(this.pageCount);
        this.pages = Array(this.pageCount)
          .fill(0)
          .map((x, i) => i);
      }),
      totalCount$.subscribe(output => {
        this.totalCount = output.data?.templates.totalCount ?? null;
      }),
      this.apollo
        .watchQuery<GetTemplateOverviewQuery>({
          query: templateOverviewGetQuery,
        })
        .valueChanges.pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(output => {
          this.currentUser = output.data?.me ?? null;
          this.labels = (output.data?.labels.edges ?? []).map(e => e.node);
          this.tags = [null, ...(output.data?.tags.edges ?? []).map(e => e.node)];
        }),
    );
  }

  setTemplateId(id: string): void {
    if (this.currentTemplateId === id) {
      this.currentTemplateId = '';
    } else {
      this.currentTemplateId = id;
    }
  }

  nextPage(): void {
    this.offset.setValue(this.offset.value + 1);
  }

  previousPage(): void {
    this.offset.setValue(this.offset.value - 1);
  }

  toContentWithTemplateId(id: string): void {
    this.router.navigate(['/content'], {queryParams: {templateId: id}});
  }

  toContentWithTemplateItemId(id: string): void {
    this.router.navigate(['/content'], {queryParams: {templateItemId: id}});
  }

  toStatisticsWithTemplateItemId(id: string): void {
    this.router.navigate(['/reports'], {queryParams: {templateItemId: id}});
  }

  toTemplateDetails(templateItemId: string): void {
    this.router.navigate(['templates', 'details', templateItemId]);
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  onSelectionChange(value: any) {
    this.selection$.next(value === 'null' ? null : value);
  }

  toggleArchived() {
    this.onlyArchived.setValue(this.onlyArchived.value === null ? false : null);
  }

  openLabelOrTagControl(type: MultiSelectType): void {
    if (type === MultiSelectType.LABEL) {
      this.labelControlOpen = !this.labelControlOpen;
      this.tagControlOpen = false;
    } else {
      this.tagControlOpen = !this.tagControlOpen;
      this.labelControlOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
}
